import type { JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import { MiniTrend } from './MiniTrend';
import { QUALITY_NAMES } from '../../engine/core/tags';

/** Read-only card for a field instrument that is not a control loop. */
export function InstrumentCard({ id }: { id: string }): JSX.Element {
  const plant = useEngine((s) => s.plant);
  const values = useEngine((s) => s.values);
  const quality = useEngine((s) => s.quality);
  const indexOf = useEngine((s) => s.indexOf);

  const cfg = plant?.instruments.find((i) => i.id === id);
  const tag = `${id}.PV`;
  const idx = indexOf.get(tag);
  const value = idx === undefined ? NaN : (values[idx] ?? NaN);
  const q = idx === undefined ? 0 : (quality[idx] ?? 0);
  const bad = q === 2;

  if (!cfg) return <div style={{ padding: 10 }}>Unknown instrument.</div>;

  const frac = Math.max(0, Math.min(1, (value - cfg.rangeLo) / Math.max(cfg.rangeHi - cfg.rangeLo, 1e-9)));

  return (
    <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8, width: 260 }}>
      <div>
        <div style={{ fontSize: 12 }}>{cfg.description}</div>
        <div className="num muted" style={{ fontSize: 11 }}>
          {tag} · {cfg.kind}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
        <span className="num" style={{ fontSize: 24, color: bad ? 'var(--pri-high)' : 'var(--text)' }}>
          {Number.isFinite(value) ? value.toFixed(cfg.decimals ?? 2) : '——'}
        </span>
        <span className="muted">{cfg.units}</span>
        {q !== 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: bad ? 'var(--pri-high)' : 'var(--pri-medium)' }}>
            {QUALITY_NAMES[q]}
          </span>
        )}
      </div>
      <div style={{ height: 6, background: 'var(--bg-deep)', border: '1px solid var(--line-dim)', borderRadius: 3 }}>
        <div style={{ width: `${frac * 100}%`, height: '100%', background: bad ? 'var(--pri-high)' : 'var(--ok)' }} />
      </div>
      <div className="muted num" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5 }}>
        <span>{cfg.rangeLo}</span>
        <span>{cfg.rangeHi}</span>
      </div>
      <MiniTrend windowS={1800} tags={[{ tag, colour: '#6fcf7d' }]} />
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', margin: 0, fontSize: 11 }}>
        <dt className="muted">Source</dt>
        <dd className="num" style={{ margin: 0 }}>
          {cfg.source}
        </dd>
        <dt className="muted">Lag</dt>
        <dd style={{ margin: 0 }}>{cfg.tau ?? 0} s</dd>
        {cfg.deadTime ? (
          <>
            <dt className="muted">Dead time</dt>
            <dd style={{ margin: 0 }}>{cfg.deadTime} s</dd>
          </>
        ) : null}
        <dt className="muted">Noise</dt>
        <dd style={{ margin: 0 }}>{(cfg.noise ?? 0).toFixed(2)} % span</dd>
      </dl>
    </div>
  );
}
